/**
 * Support chat — the producer side of the buyer|producer ↔ admin channel.
 *
 * Everything talks to /chat/*, which the server scopes to the caller's OWN thread; there is
 * no conversation id to pass. The other party is always "support" — no DTO in here can name
 * a person, and the UI never pretends otherwise.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChatConversation, ChatMessage, ChatMessagePage } from '@most/api-types';
import type { CallApi, CallKind, ChatCallDTO } from '@most/webrtc-call';
import { request, upload } from './http';
import { qk } from './queryClient';

// ── REST ─────────────────────────────────────────────────────────────────────

export const getChatConversation = () => request<ChatConversation>('/chat');

export const listChatMessages = (cursor?: string, signal?: AbortSignal) =>
  request<ChatMessagePage>(`/chat/messages${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`, { signal });

export const sendChatMessage = (body: string) =>
  request<ChatMessage>('/chat/messages', { method: 'POST', body: { body } });

export type ChatUploadKind = 'image' | 'video' | 'audio' | 'file';

export const uploadChatAttachment = (kind: ChatUploadKind, file: File | Blob, caption?: string) => {
  const fd = new FormData();
  fd.append('kind', kind);
  if (caption) fd.append('body', caption);
  fd.append('file', file, file instanceof File ? file.name : 'recording.webm');
  return upload<ChatMessage>('/chat/attachments', fd);
};

export const markChatRead = () => request<{ updated_count: number }>('/chat/read', { method: 'POST' });

/** The transport adapter for @most/webrtc-call. */
export const chatCallApi: CallApi = {
  start: (kind: CallKind) => request<ChatCallDTO>('/chat/calls', { method: 'POST', body: { kind } }),
  get: (id) => request<ChatCallDTO>(`/chat/calls/${id}`),
  accept: (id) => request<ChatCallDTO>(`/chat/calls/${id}/accept`, { method: 'POST' }),
  decline: (id) => request<ChatCallDTO>(`/chat/calls/${id}/decline`, { method: 'POST' }),
  end: (id) => request<ChatCallDTO>(`/chat/calls/${id}/end`, { method: 'POST' }),
  postSignal: async (id, data) => {
    await request(`/chat/calls/${id}/signals`, { method: 'POST', body: { data } });
  },
  getSignals: async (id) => {
    const res = await request<{ signals: string[] }>(`/chat/calls/${id}/signals`);
    return res.signals;
  },
};

// ── Hooks ────────────────────────────────────────────────────────────────────

export function useChatConversation(enabled = true) {
  return useQuery({ queryKey: qk.chat, queryFn: getChatConversation, enabled, staleTime: 15_000 });
}

export function useChatMessages(enabled = true) {
  return useInfiniteQuery({
    queryKey: qk.chatMessages,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) => listChatMessages(pageParam, signal),
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled,
    staleTime: 10_000,
  });
}

function invalidateThread(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: qk.chatMessages });
  void qc.invalidateQueries({ queryKey: qk.chat });
}

export function useSendChatMessage() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: sendChatMessage, onSuccess: () => invalidateThread(qc) });
}

export function useUploadChatAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, file, caption }: { kind: ChatUploadKind; file: File | Blob; caption?: string }) =>
      uploadChatAttachment(kind, file, caption),
    onSuccess: () => invalidateThread(qc),
  });
}

export function useMarkChatRead() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: markChatRead, onSuccess: () => invalidateThread(qc) });
}

// ── WS bridge ────────────────────────────────────────────────────────────────
//
// The socket lives in shared/ws.ts (one per shell); the call engine lives in the widget.
// This tiny bus forwards call/chat nudges between them — {type, id} only, like the wire.

type ChatWsEvent = { type: string; id?: string };
type Listener = (event: ChatWsEvent) => void;

const listeners = new Set<Listener>();

export const chatEvents = {
  emit(event: ChatWsEvent): void {
    for (const l of listeners) l(event);
  },
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};
